import { AddressBadge, TxHashBadge } from './AddressBadge';
import { timeAgo, getTokenColor } from '../lib/utils';
import { Skeleton } from './ui/skeleton';
import { Badge } from './ui/badge';
import { ArrowRight, ArrowDownLeft, ArrowUpRight, Repeat } from 'lucide-react';

export function TransactionsTable({ transactions, loading, currentAddress = null }) {
  if (loading) {
    return (
      <div className="table-container rounded-lg border">
        <table className="data-table">
          <thead>
            <tr>
              <th>Tx Hash</th>
              <th>Type</th>
              <th>Token</th>
              <th>From</th>
              <th></th>
              <th>To</th>
              <th className="text-right">Amount</th>
              <th className="text-right">Time</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 5 }).map((_, i) => (
              <tr key={i}>
                <td><Skeleton className="h-6 w-28" /></td>
                <td><Skeleton className="h-6 w-16" /></td>
                <td><Skeleton className="h-6 w-16" /></td>
                <td><Skeleton className="h-6 w-24" /></td>
                <td><Skeleton className="h-4 w-4" /></td>
                <td><Skeleton className="h-6 w-24" /></td>
                <td><Skeleton className="h-6 w-20 ml-auto" /></td>
                <td><Skeleton className="h-6 w-16 ml-auto" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (!transactions || transactions.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>No transactions found</p>
      </div>
    );
  }

  // Get direction relative to current address
  const getDirection = (tx) => {
    if (!currentAddress) return null;
    const normalizedCurrent = currentAddress.toLowerCase();
    const from = tx.from_address?.toLowerCase();
    const to = tx.to_address?.toLowerCase();
    
    if (to === normalizedCurrent) return 'IN';
    if (from === normalizedCurrent) return 'OUT';
    return null;
  };

  return (
    <div className="table-container rounded-lg border overflow-x-auto">
      <table className="data-table">
        <thead>
          <tr>
            <th>Tx Hash</th>
            <th>Type</th>
            <th>Token</th>
            <th>From</th>
            <th className="text-center w-8"></th>
            <th>To</th>
            <th className="text-right">Amount</th>
            <th className="text-right">Time</th>
          </tr>
        </thead>
        <tbody>
          {transactions.map((tx, idx) => {
            const tokenColors = getTokenColor(tx.token_symbol);
            const direction = currentAddress ? getDirection(tx) : null;
            
            // Determine transaction type badge
            let typeBadge;
            if (tx.is_swap && tx.swap_type) {
              typeBadge = (
                <Badge className={`text-xs font-bold flex items-center gap-1 w-fit ${
                  tx.swap_type === 'buy' 
                    ? 'bg-emerald-500/20 text-emerald-600 border-emerald-500/30' 
                    : 'bg-red-500/20 text-red-600 border-red-500/30'
                }`}>
                  <Repeat className="w-3 h-3" />
                  {tx.swap_type === 'buy' ? 'BUY' : 'SELL'}
                </Badge>
              );
            } else if (direction) {
              typeBadge = (
                <Badge className={`text-xs font-bold flex items-center gap-1 w-fit ${
                  direction === 'IN' 
                    ? 'bg-emerald-500/20 text-emerald-600 border-emerald-500/30' 
                    : 'bg-amber-500/20 text-amber-600 border-amber-500/30'
                }`}>
                  {direction === 'IN' ? <ArrowDownLeft className="w-3 h-3" /> : <ArrowUpRight className="w-3 h-3" />}
                  {direction}
                </Badge>
              );
            } else {
              typeBadge = (
                <Badge variant="outline" className="text-xs">
                  Transfer
                </Badge>
              );
            }
            
            return (
              <tr key={`${tx.tx_hash}-${tx.log_index}-${idx}`} className="animate-fade-in">
                <td>
                  <TxHashBadge hash={tx.tx_hash} />
                </td>
                <td>
                  {typeBadge}
                </td>
                <td>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${tokenColors.bg} ${tokenColors.text}`}>
                    {tx.token_symbol || 'Unknown'}
                  </span>
                </td>
                <td>
                  <AddressBadge address={tx.from_address} highlight={currentAddress && tx.from_address?.toLowerCase() === currentAddress.toLowerCase()} />
                </td>
                <td className="text-center">
                  <ArrowRight className="w-4 h-4 text-muted-foreground inline" />
                </td>
                <td>
                  <AddressBadge address={tx.to_address} highlight={currentAddress && tx.to_address?.toLowerCase() === currentAddress.toLowerCase()} />
                </td>
                <td className={`text-right font-mono ${
                  direction === 'IN' ? 'text-emerald-600' : 
                  direction === 'OUT' ? 'text-red-600' : ''
                }`}>
                  {direction === 'IN' && '+'}{direction === 'OUT' && '-'}{tx.amount_formatted}
                </td>
                <td className="text-right text-muted-foreground text-sm">
                  {timeAgo(tx.block_time)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
